import React, { useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import * as CONSTANT from "../../constants";
import styleClasses from "./order.module.scss";
import ItemDetailMenuCategory from "./ItemDetailMenuCategory/ItemDetailMenuCategory";
import ItemScript from "../../components/scripts/itemScript";
import SelectMenuItemScript from "../../components/scripts/selectMenuItemScript";
import { useAuthState } from "../../context/UserAuthentication/context";
import { useStoreLocation } from "../../context/StoreLocation";
import useTrackBarVisibility from "../../hooks/useTrackBarVisibility";
import { useIsDesignV2 } from "../../hooks/useIsDesignV2";
import FavoriteButton from "../../containers/try2/Customization/FavoriteButton";
import ItemDetail from "../../components/OrderAnItem/ItemCustomization/ItemDetail";
import ItemSizeCard from "../../components/OrderAnItem/ItemCustomization/SizeCard";
import ItemsBox from "../../components/OrderAnItem/SelectedItems/ItemsBox";
import { OrderNameForm } from "../../components/OrderAnItem/OrderNameForm/OrderNameForm";
import Button from "../../components/Buttons/Button";
import CustomizeButton from "../../components/OrderAnItem/Buttons/customizeButton";
import MakeItTryTwoCombo from "../../components/OrderAnItem/ItemCustomization/MakeItTryTwoCombo";
import useCheckMobileScreen from "../../hooks/useCheckMobileScreen";
import { useAppSelector } from "../../redux/store/store";
import {
  availableForSelectedOrderType,
  verifyStatus,
} from "../../helpers/customizedItem/customizedItem.helper";
import {
  isGuestUser,
  isItCreateYourOwnItem,
  showMakeItTryToCombo,
} from "../../helpers/helperMethods";
import useGetMenuCategories from "../../react-query-hooks/useGetCategories";
import Spinner from "../../components/Spinner/Spinner";
import useCategories from "../../react-query-hooks/useCategories";
import useFavorites from "../../react-query-hooks/useFavorites";
import useRemoveFavorite from "../../react-query-hooks/Favorites/useRemoveFavorite";
import useAddFavorites from "../../react-query-hooks/Favorites/useAddFavorite";
import _debounce from "lodash/debounce";

export default function StaticOrder() {
  const { authInfo } = useAuthState();
  let location = useLocation();
  const navigate = useNavigate();
  const { locationInfo } = useStoreLocation();
  const isDesignV2 = useIsDesignV2();
  const isCheckTrackBar = useTrackBarVisibility();
  const query = new URLSearchParams(location.search);
  const category_id = query.get("category_id");
  let { id: item_id } = useParams<any>();
  if (item_id?.includes("?")) {
    item_id = item_id.split("?")[0];
  }

  const { data: menus = [], isLoading } = useCategories(null, authInfo);
  const categories: any[] = (menus as any)?.categories ?? (menus as any) ?? [];
  const activeCategory: any = categories?.find(
    (c: any) => String(c?.id) === String(category_id),
  );
  const activeItem: any = activeCategory?.items?.find(
    (it: any) => String(it?.id) === String(item_id),
  );

  useEffect(() => {
    if (
      !isLoading &&
      categories.length > 0 &&
      (!activeCategory || !activeItem)
    ) {
      if (activeCategory && !activeItem) {
        // Redirect to menu/activeCategoryName
        const categoryName = activeCategory?.name;
        navigate(`${CONSTANT.ROUTE_MENU}/${categoryName}`, { replace: true });
      } else {
        navigate(CONSTANT.ROUTE_MENU, { replace: true });
      }
    }
  }, [isLoading, categories, activeCategory, activeItem]);

  const order: any = {
    modifiers: [{ modifiers: [{ id: 1, name: "Romaine" }] }],
    size: null,
    display_price: 0,
  };
  const item: any = activeItem
    ? {
        ...activeItem,
        category_id: activeCategory?.id ?? activeItem?.category_id,
        category_name: activeCategory?.name ?? activeItem?.category_name,
        image:
          activeItem?.image ||
          activeItem?.app_image ||
          activeItem?.web_image ||
          activeItem?.image_url ||
          "/Items/placeholder.webp",
        image_version: activeItem?.image_version ?? "1",
        is_customizable: activeItem?.is_customizable ?? true,
        item_location: activeItem?.item_location ?? [],
        is_active: activeItem?.is_active ?? true,
        tags: activeItem?.tags ?? [],
        description: activeItem?.description ?? activeItem?.desc ?? "",
      }
    : null;
  // Local UI state for design-only rendering
  const [quantity, setQuantity] = useState<number>(1);
  const [madeForName, setMadeForName] = useState<string>("");
  const [isSize, setIsSize] = useState<boolean>(false);
  const [showCustomization, setShowCustomization] = useState<boolean>(false);
  const [showCustomizedOptions, setShowCustomizedOptions] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const showFooterButtonRef = useRef<HTMLDivElement>(null);
  const cart = useAppSelector((state) => state.cart);
  const isCreateYourOwnItem = isItCreateYourOwnItem(item?.is_create_your_own);
  const { data: favorites = [] } = useFavorites(authInfo.userId, authInfo.type);
  const [favoriteItems, setFavoriteItems] = useState<any>({});
  let { mutateAsync: removeFavorite } = useRemoveFavorite();
  let { mutateAsync: addFavorite } = useAddFavorites();

  useEffect(() => {
    if (favorites.length > 0) {
      const favouriteItem = favorites?.find((fvrt) => fvrt.item_id === item.id);
      setFavoriteItems(favouriteItem ? favouriteItem : {});
    }
  }, [favorites]);

  const removeFavoriteItem = (favoriteItemId: any, itm: any) => {
    const userId = authInfo.userId;
    removeFavorite(
      { itemId: favoriteItemId, userId },
      {
        onError: () => {
          setFavoriteItems(itm);
        },
      },
    );
  };

  const addFavoriteItem = (item: any) => {
    addFavorite(
      { ...item, customer_id: authInfo.userId },
      {
        onError: (error, variables, context) => {
          setFavoriteItems({});
        },
      },
    );
  };

  const debouncedAddFavourite = React.useCallback(
    _debounce(addFavoriteItem, 500),
    [],
  );
  const debouncedRemoveFavourite = React.useCallback(
    _debounce(removeFavoriteItem, 500),
    [],
  );

  const favoriteButtonHandler = (
    e: React.MouseEvent<HTMLButtonElement>,
    item,
  ) => {
    if (Object.keys(favoriteItems)?.length <= 0) {
      setFavoriteItems(item);
      debouncedAddFavourite(item);
    } else {
      setFavoriteItems({});
      debouncedRemoveFavourite(item?.id, item);
    }
  };

  const showFavrtButton = () => {
    if (
      authInfo?.userId &&
      !isCreateYourOwnItem
    ) {
      return true;
    }
    return false;
  };

  const CustomizeButtonShow = () => {
    if (isCreateYourOwnItem || !item?.is_customizable) {
      return false;
    } else {
      return true;
    }
  };
  const showCustomizeButton = CustomizeButtonShow();

  const availableForOrderType = () =>
    cart?.isOrderTypeSelected &&
    availableForSelectedOrderType(item, cart?.orderType);
  const isItemAvailable = () =>
    item.is_active && verifyStatus(item, "item_location");
  const isItSandWichAndNotMultiSize = () => {
    if (
      item?.category_id?.toString() === CONSTANT.REACT_APP_SANDWICHES_ID &&
      !item?.half_brink_item_id
    )
      return true;
    else {
      if (item?.half_brink_item_id) return true;
      else return false;
    }
  };
  const isNavigatingToCombo = useRef<boolean>(false);
  const reward_size = null as any;
  const isItemRedeemed = false;
  const isMobile = useCheckMobileScreen();

  // No-op handlers and calculators for design-only
  const getItemTotalCalories = () => 0;
  const getItemTotal = () => 0 as any;
  const handleItemSizeChange = (_e: any) => {};
  const handelRedeemSize = () => {};
  const handleSyncIngredients = (_item: any) => {};
  const handleIngredientSelection = (_data: any) => {};
  const openLocationSelectionModal = () => {
    window.dispatchEvent(new CustomEvent("openLocationSelectionModal"));
  };
  const onCustomizeClick = (_type: string) => {
    openLocationSelectionModal();
    return false;
  };
  const showModifierModal = () => {};
  const getOrderButtonState = (_type: any) => ({ status: true as boolean });
  const handleMakeItComboValidation = () => {
    openLocationSelectionModal();
    return false; // Always return false for design-only component
  };
  const setFirstTimeModal = (_v: boolean) => {};
  const selectorWrapperVariant = isDesignV2
    ? showMakeItTryToCombo(item?.category_name) &&
      !showCustomization &&
      isItemAvailable() &&
      isItSandWichAndNotMultiSize() &&
      (order?.size == CONSTANT.HALF_SIZE?.upper_case ||
        !item.half_brink_item_id)
      ? styleClasses.selector_wrapper_with_try2combo
      : styleClasses.selector_wrapper_without_try2combo
    : "testing";

  const orderedIngredients = order?.modifiers?.[0]?.modifiers || [];

  if (isLoading) return <Spinner />;
  if (!item) return <div className="text-center py-4">Item not found</div>;

  return (
    <>
      <SelectMenuItemScript
        item={item}
        scriptType={"select_item"}
        location_id={locationInfo?.selectedStore?.id}
        userId={authInfo.userId}
      />
      <ItemScript
        item={item}
        scriptType={"view_item"}
        location_id={locationInfo?.selectedStore?.id}
        userId={authInfo.userId}
      />
      {order?.modifiers?.length > 0 && (
        <div className={styleClasses.order_content_wrapper}>
          {!isDesignV2 && <ItemDetailMenuCategory category_id={category_id} />}
          {isDesignV2 && (
            <div
              className={`text-start mt-2 mb-2 ms-2 ${styleClasses.category_back_button_mobile_designv2} ${
                isCheckTrackBar
                  ? styleClasses.track_here_order_category_back_button_mobile_designv2
                  : styleClasses.no_track_order_category_back_button_mobile_designv2
              }`}
            >
              <Link
                to={
                  item?.category_name
                    ? CONSTANT.ROUTE_MENU + "/" + item.category_name
                    : CONSTANT.ROUTE_MENU
                }
                className="btn btn-custom back-arrow-btn f-s14 px-0 mb-0"
              >
                {item?.category_name ?? "Menu"}
              </Link>
            </div>
          )}
          <Container fluid>
            <div
              className={`${styleClasses.item_main_div} ${isDesignV2 ? styleClasses.item_main_div_mobile_designv2 : ""} d-flex flex-column h-100`}
            >
              <Row
                className={`align-items-start ${styleClasses.single_item_detail_wrapper} ${
                  isCheckTrackBar
                    ? styleClasses.track_here_order_detail_wrapper
                    : styleClasses.no_track_order_detail_wrapper
                }`}
              >
                <Col lg="7">
                  <div className={styleClasses.item_img_wrap}>
                    <img
                      src={`${CONSTANT.REACT_APP_CLOUD_FRONT}${item.image}?v=${item?.image_version || Date.now()}`}
                      className="img-fluid"
                      alt={item.name}
                    />
                  </div>
                </Col>
                <Col
                  lg="5"
                  md="11"
                  className={`${styleClasses.item_info_wrap} single-item-info-wrap`}
                >
                  <>
                    {showFavrtButton() && (
                      <FavoriteButton
                        favIcon={true}
                        buttonHandler={favoriteButtonHandler}
                        className="d-none d-lg-block position-static ms-auto mb-2 mt-4"
                        item={item}
                        favourite={
                          Object.keys(favoriteItems)?.length > 0 ? true : false
                        }
                        isIconblueFill={false}
                      />
                    )}
                    <ItemDetail
                      name={item?.name}
                      item={item}
                      description={item?.description}
                      styleClasses={styleClasses}
                      unAvailableModifiers={false}
                      tags={item?.tags}
                      favoriteButtonHandler={favoriteButtonHandler}
                      favourite={Object.keys(favoriteItems)?.length > 0}
                      authInfo={authInfo}
                    />
                    {isItemAvailable() ? (
                      <div
                        className={`${styleClasses.selector_wrapper} pt-2 single-itemOrder-wrap ${selectorWrapperVariant}`}
                      >
                        {!isMobile && <hr />}

                        {!isDesignV2 && (
                          <div
                            className={`${
                              (!showMakeItTryToCombo(item?.category_name) ||
                                showCustomization) &&
                              "flex-column"
                            } single-item-btn-wrap d-flex align-items-center`}
                            ref={showFooterButtonRef}
                            data-testid="div-add-to-order"
                          >
                            {isItemAvailable() ? (
                              <Button
                                type="button"
                                className={`mw-100 btn-disabled-bg add-to-order-right btn-large`}
                                id={
                                  !showCustomization
                                    ? "add-to-order-button"
                                    : "update-order-button"
                                }
                                onClick={openLocationSelectionModal}
                                loading={loading}
                                LoaderPosition={`loader-position`}
                              >
                                {!showCustomization
                                  ? "ADD TO ORDER"
                                  : "UPDATE ORDER"}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                className={`mw-100 btn-disabled-bg add-to-order-right`}
                                disabled={true}
                                loading={loading}
                                LoaderPosition={`loader-position`}
                              >
                                Unavailable
                              </Button>
                            )}
                            {showCustomizeButton && isItemAvailable() ? (
                              <CustomizeButton
                                item={item}
                                handleSyncIngredients={handleSyncIngredients}
                                setShowCustomizedOptions={
                                  setShowCustomizedOptions
                                }
                                getOrderButtonState={(type: string) =>
                                  onCustomizeClick(type)
                                }
                                showModifierModal={showModifierModal}
                              />
                            ) : null}
                          </div>
                        )}
                        {showMakeItTryToCombo(item?.category_name) &&
                        !showCustomization &&
                        isItemAvailable() &&
                        isItSandWichAndNotMultiSize() &&
                        !isDesignV2 ? (
                          <div>
                            <MakeItTryTwoCombo
                              handleSyncIngredients={handleSyncIngredients}
                              item={item}
                              order={order}
                              getOrderButtonState={getOrderButtonState}
                              handleIngredientSelection={
                                handleIngredientSelection
                              }
                              showCustomizedOptions={showCustomizedOptions}
                              showCustomization={showCustomization}
                              stickyAddToCartStyle={false}
                              isNavigatingToCombo={isNavigatingToCombo}
                              handleMakeItComboValidation={
                                handleMakeItComboValidation
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-large w-100 mx-auto mb-2"
                        disabled={true}
                      >
                        Unavailable
                      </button>
                    )}
                    <div
                      className={` ${
                        isCreateYourOwnItem
                          ? "gap-2"
                          : `flex-wrap ${isDesignV2 ? "gap-0" : "gap-3"}`
                      } sticky-add-to-cart d-flex justify-content-center px-3`}
                    >
                      <div
                        className={`${
                          isDesignV2 &&
                          !isCreateYourOwnItem &&
                          "sticky-btn-wrap-design-v2"
                        } ${
                          isCreateYourOwnItem
                            ? "salad-sticky-btn"
                            : "sticky-btn-wrap justify-content-center"
                        } d-flex single-item-btn-wrap mb-0 w-100 ${!showCustomizeButton && "only-add-to-order-button"}`}
                      >
                        {isItemAvailable() ? (
                          <Button
                            type="button"
                            className={`mw-100 btn-disabled-bg add-to-order-right createSaladOrderBtn ${!!isDesignV2 && "f-sm-s16"}`}
                            loading={loading}
                            onClick={openLocationSelectionModal}
                            LoaderPosition={`loader-position`}
                            id={
                              !showCustomization
                                ? "add-to-order-button"
                                : "update-order-button"
                            }
                          >
                            {!showCustomization
                              ? "ADD TO ORDER"
                              : "UPDATE ORDER"}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            className={`mw-100 btn-disabled-bg add-to-order-right createSaladOrderBtn ${!!isDesignV2 && "f-sm-s16"}`}
                            disabled={true}
                            loading={loading}
                            LoaderPosition={`loader-position`}
                            id={
                              !showCustomization
                                ? "add-to-order-button"
                                : "update-order-button"
                            }
                          >
                            {"Unavailable"}
                          </Button>
                        )}
                        {showCustomizeButton && isItemAvailable() ? (
                          <CustomizeButton
                            item={item}
                            handleSyncIngredients={handleSyncIngredients}
                            setShowCustomizedOptions={setShowCustomizedOptions}
                            getOrderButtonState={(type: string) =>
                              onCustomizeClick(type)
                            }
                            showModifierModal={showModifierModal}
                          />
                        ) : null}
                      </div>
                      {showMakeItTryToCombo(item?.category_name) &&
                      !showCustomization &&
                      isItemAvailable() &&
                      isItSandWichAndNotMultiSize() ? (
                        <div
                          className={`${isDesignV2 && "pt-3"} ${isCreateYourOwnItem ? "saladCombo" : `combo ${isMobile && "w-100"}`} `}
                        >
                          <MakeItTryTwoCombo
                            handleSyncIngredients={handleSyncIngredients}
                            item={item}
                            order={order}
                            getOrderButtonState={getOrderButtonState}
                            handleIngredientSelection={
                              handleIngredientSelection
                            }
                            showCustomizedOptions={showCustomizedOptions}
                            showCustomization={showCustomization}
                            stickyAddToCartStyle={true}
                            isNavigatingToCombo={isNavigatingToCombo}
                            handleMakeItComboValidation={
                              handleMakeItComboValidation
                            }
                          />
                        </div>
                      ) : null}
                    </div>
                  </>
                </Col>
              </Row>
            </div>
          </Container>
        </div>
      )}
    </>
  );
}
